import React, { useCallback, useEffect } from "react";

import BondValuationActions from "./store/actions";
import { FilterData, FilterDataItem } from "./store/model";
import { useAppDispatch, useAppSelector } from "application/store";
import { requestSelector } from "common/responseHandlerService/slice";
import {
  homeSelector,
  setFilterIndices,
  setFilterMetric,
  resetState,
} from "./store/slice";

import Chart from "features/chart";
import DropGroup from "application/components/DropGroup";

import {
  Title,
  DropContainer,
  FiltersContainer,
  GoToSummaryTableButton,
  GoToSummaryTableIcon,
} from "./styles";
import MainContainer from "../../application/components/MainContainer";
import { useMediaQuery, useTheme } from "@mui/material";
import { PathNames } from "application/routes";

const ROUND_DECIMALS_TO_ZERO = ["market_value", "number_of_issues"];
const METRICS_WITH_TWO_DECIMAL_Y_LABEL = [
  "average_maturity",
  "modified_duration",
  "duration_to_worst",
  "convexity",
];

const GoToSummaryTable = ({ onClick }: { onClick: () => void }) => {
  return (
    <GoToSummaryTableButton onClick={onClick}>
      <GoToSummaryTableIcon />
      <p>Go to summary table</p>
    </GoToSummaryTableButton>
  );
};

const FixedIncomeCharts = () => {
  const dispatch = useAppDispatch();
  const requestData = useAppSelector(requestSelector);
  const { metrics, indices, indicesData, filters } =
    useAppSelector(homeSelector);
  const loading =
    requestData.getIndices.loading ||
    requestData.getIndicesData.loading ||
    requestData.getMetrics.loading;

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down(1024));

  useEffect(() => {
    if (!indicesData.data.length) {
      Promise.all([
        dispatch(BondValuationActions.getMetrics()),
        dispatch(BondValuationActions.getIndices()),
      ]).then((resArr) => {
        const metricRes = resArr[0].payload as FilterData;
        const indicesRes = resArr[1].payload as FilterData;
        const metricKeys = Object.keys(metricRes);
        const indicesKeys = Object.keys(indicesRes);

        const firstIndices = indicesRes[indicesKeys[0]][0];
        const firstMetric = metricRes[metricKeys[0]][0];
        handleIndicesSelect(firstIndices);
        handleMetricSelect(firstMetric);
      });
    }
    return () => {
      dispatch(resetState());
    };
  }, []);

  useEffect(() => {
    if (filters.selectedIndices.code && filters.selectedMetric.code) {
      dispatch(BondValuationActions.getIndicesData());
    }
  }, [filters]);

  const handleIndicesSelect = (item: FilterDataItem) => {
    dispatch(setFilterIndices(item));
  };

  const handleMetricSelect = (item: FilterDataItem) => {
    dispatch(setFilterMetric(item));
  };

  const handleGoToSummaryTable = useCallback(() => {
    window.open(
      `${window.origin}${PathNames.bondValuationSummary}?metric=${filters.selectedMetric.code}`,
      "_blank"
    );
  }, [filters]);

  return (
    <MainContainer>
      <FiltersContainer>
        <Title>Bond Yield, Duration, and Spread Charts</Title>
        <DropContainer>
          <DropGroup
            data={indices}
            label="Index"
            disabled={loading}
            onSelect={handleIndicesSelect}
            selected={filters.selectedIndices}
            placeholder="Index name goes here"
          />
          <DropGroup
            data={metrics}
            label="Metric"
            disabled={loading}
            onSelect={handleMetricSelect}
            selected={filters.selectedMetric}
            placeholder="Metric name goes here"
          />
          <GoToSummaryTable onClick={handleGoToSummaryTable} />
        </DropContainer>
      </FiltersContainer>

      <Chart
        loading={loading}
        indicesData={indicesData}
        filters={filters}
        isTablet={isTablet}
        withNumberFormater
        paddingLeft={60}
        averageWidth="82"
        isBloomberg
        numberOfDecimalsYLabels={
          METRICS_WITH_TWO_DECIMAL_Y_LABEL.includes(filters.selectedMetric.code)
            ? 2
            : undefined
        }
        doNotShowDecimals={ROUND_DECIMALS_TO_ZERO.includes(
          filters.selectedMetric.code
        )}
      />
    </MainContainer>
  );
};

export default FixedIncomeCharts;
